import React from "react";
import { Icon } from "@iconify/react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ImgOptimized from "../../../components/ImgOptimized";
import ImgDrop from "../../../assets/na7p8lprxplx309m10.png";
import { getMinioSize } from "../../../utils/helpers";

const SortableImage = ({ id, index, total, onMove, onRemove, canEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative h-24 rounded overflow-hidden bg-gray-100 ${
        isDragging ? "ring-2 ring-teal-500 shadow-lg opacity-90" : ""
      } ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
      {...attributes}
      {...listeners}
    >
      <ImgOptimized
        src={`${process.env.REACT_APP_BACK_URL}/api/minio/${id}${getMinioSize()}`}
        alt={`Top image ${index + 1}`}
        className="w-full h-24 object-cover pointer-events-none select-none"
        draggable={false}
      />
      <span className="absolute top-1 left-1 bg-black/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
        {index === 0 ? "1 · Main" : index + 1}
      </span>
      {canEdit && (
        <>
          <button
            type="button"
            title="Remove"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onRemove(index)}
            className="absolute top-1 right-1 bg-red-500 text-white rounded-full size-5 grid place-content-center"
          >
            <Icon icon="mage:trash" className="size-3" />
          </button>
          <div className="absolute bottom-1 inset-x-1 flex justify-between">
            <button
              type="button"
              title="Move earlier"
              disabled={index === 0}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onMove(index, index - 1)}
              className="bg-white/90 rounded-full size-5 grid place-content-center disabled:opacity-30"
            >
              <Icon icon="mdi:chevron-left" />
            </button>
            <button
              type="button"
              title="Move later"
              disabled={index === total - 1}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onMove(index, index + 1)}
              className="bg-white/90 rounded-full size-5 grid place-content-center disabled:opacity-30"
            >
              <Icon icon="mdi:chevron-right" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Selected images in display order; drag (or use the arrows) to reorder.
const SortableImagePreviewer = ({
  images = [],
  onChange,
  canEdit = true,
  grid = 5,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const move = (from, to) => onChange(arrayMove(images, from, to));
  const remove = (index) => onChange(images.filter((_, i) => i !== index));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(images.indexOf(active.id), images.indexOf(over.id));
  };

  return (
    <div className="border w-full rounded-lg p-4 min-h-[100px] bg-white">
      {images.length ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={images} strategy={rectSortingStrategy}>
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${grid}, minmax(0, 1fr))`,
              }}
            >
              {images.map((img, index) => (
                <SortableImage
                  key={img}
                  id={img}
                  index={index}
                  total={images.length}
                  onMove={move}
                  onRemove={remove}
                  canEdit={canEdit}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="w-full h-full grid place-content-center">
          <ImgOptimized
            className="w-[200px]"
            src={ImgDrop}
            alt="No images selected"
          />
        </div>
      )}
    </div>
  );
};

export default SortableImagePreviewer;
